import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
      CButton,
      CForm,
      CFormLabel,
      CFormInput,
      CFormSelect,
      CCollapse,
} from '@coreui/react';

// Constants
const businessTypes = ['individual', 'company', 'other'];
const businessTypeLabels = {
      individual: 'Independent Business',
      company: 'Business Chain',
      other: 'Other',
};

const stripeDashboardTypes = ['none', 'full', 'express'];
const stripeDashboardTypeLabels = {
      none: 'No access for connected accounts',
      full: 'Full Stripe Dashboard',
      express: 'Express Dashboard',
};

const paymentLosses = ['stripe', 'application'];
const paymentLossesLabels = {
      stripe: 'Stripe',
      application: 'Application',
};

const feePayers = ['account', 'application'];
const feePayerLabels = {
      account: 'Stripe collects fees from connected accounts',
      application: 'Stripe collects fees from Application',
};

const BusinessDetailsForm = ({ authUser, onSubmit }) => {
      const [showMoreOptions, setShowMoreOptions] = useState(false);
      const [loading, setLoading] = useState(false);

      const { register, handleSubmit, watch, setValue, control, formState: { errors } } = useForm({
            defaultValues: {
                  businessType: 'individual',
                  businessName: '',
                  country: 'US',
                  stripeDashboardType: 'none',
                  paymentLosses: 'stripe',
                  feePayer: 'account',
            }
      });

      // Watch for changes in account controller properties
      const watchAccountControllerProperties = watch([
            'stripeDashboardType',
            'paymentLosses',
            'feePayer',
      ]);

      useEffect(() => {
            const values = watch();
            if (values.stripeDashboardType === 'full' &&
                  (values.paymentLosses !== 'stripe' || values.feePayer !== 'account')) {
                  setValue('paymentLosses', 'stripe');
                  setValue('feePayer', 'account');
            } else if (values.stripeDashboardType === 'express' &&
                  (values.paymentLosses !== 'application' || values.feePayer !== 'application')) {
                  setValue('paymentLosses', 'application');
                  setValue('feePayer', 'application');
            }
      }, [watchAccountControllerProperties, setValue, watch]);

      const handleFormSubmit = async (data) => {
            setLoading(true);
            try {
                  await onSubmit(data);
            } catch (error) {
                  console.error('Error submitting form:', error);
                  toast.error('Failed to submit business details');
            } finally {
                  setLoading(false);
            }
      };

      return (
            <CForm onSubmit={handleSubmit(handleFormSubmit)} className="mt-4">
                  <div className="mb-3">
                        <CFormLabel>Business Type</CFormLabel>
                        {businessTypes.map((type) => (
                              <div key={type} className="form-check">
                                    <input
                                          type="radio"
                                          className="form-check-input"
                                          {...register('businessType')}
                                          value={type}
                                          id={`businessType-${type}`}
                                    />
                                    <label className="form-check-label" htmlFor={`businessType-${type}`}>
                                          {businessTypeLabels[type]}
                                    </label>
                              </div>
                        ))}
                  </div>

                  <div className="mb-3">
                        <CFormLabel>Business Name</CFormLabel>
                        <CFormInput
                              {...register('businessName', { required: true })}
                              placeholder="My business name"
                        />
                        {errors.businessName && <span className="text-danger">This field is required</span>}
                  </div>

                  <CButton
                        color="link"
                        onClick={() => setShowMoreOptions(!showMoreOptions)}
                        className="mb-3"
                  >
                        {showMoreOptions ? 'Show less options' : 'Show more options'}
                  </CButton>

                  <CCollapse visible={showMoreOptions}>
                        <div className="mb-3">
                              <CFormLabel>Stripe Dashboard Access</CFormLabel>
                              <Controller
                                    name="stripeDashboardType"
                                    control={control}
                                    render={({ field }) => (
                                          <CFormSelect {...field}>
                                                {stripeDashboardTypes.map((type) => (
                                                      <option key={type} value={type}>
                                                            {stripeDashboardTypeLabels[type]}
                                                      </option>
                                                ))}
                                          </CFormSelect>
                                    )}
                              />
                        </div>

                        <div className="mb-3">
                              <CFormLabel>Negative Balance Liability</CFormLabel>
                              <Controller
                                    name="paymentLosses"
                                    control={control}
                                    render={({ field }) => (
                                          <CFormSelect {...field}>
                                                {paymentLosses.map((type) => (
                                                      <option key={type} value={type}>
                                                            {paymentLossesLabels[type]}
                                                      </option>
                                                ))}
                                          </CFormSelect>
                                    )}
                              />
                        </div>

                        <div className="mb-3">
                              <CFormLabel>Stripe Fee Collection</CFormLabel>
                              <Controller
                                    name="feePayer"
                                    control={control}
                                    render={({ field }) => (
                                          <CFormSelect {...field}>
                                                {feePayers.map((type) => (
                                                      <option key={type} value={type}>
                                                            {feePayerLabels[type]}
                                                      </option>
                                                ))}
                                          </CFormSelect>
                                    )}
                              />
                        </div>
                  </CCollapse>

                  <CButton
                        type="submit"
                        color="primary"
                        disabled={loading}
                        className="w-100"
                  >
                        {loading ? 'Loading...' : 'Continue'}
                  </CButton>
            </CForm>
      );
};

export default BusinessDetailsForm; 